import {
  ComponentPropsWithoutRef,
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
} from "react";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

import { TokenObj } from "@servicetitan/hammer-token/type/types";
import { kebabize } from "../../functions";
import { type Token } from "@servicetitan/hammer-token";
import styles from "./ThemeProvider.module.scss";

type TokenObjType = {
  [key: string]: TokenObj;
};

export type CustomThemeType = {
  primitive?: Partial<Token["primitive"]>;
  semantic?: Partial<Token["semantic"]>;
  component?: Partial<Token["component"]>;
};

export type ThemeProviderProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    theme?: CustomThemeType;
    mode?: "light" | "dark";
    children?: ReactNode;
  };

export type ThemeProviderContextProps = {
  theme?: CustomThemeType;
  mode?: "light" | "dark";
};
export const ThemeProviderContext = createContext<ThemeProviderContextProps>(
  {},
);

export const ThemeProvider: FC<ThemeProviderProps> = (props) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    theme,
    mode: modeProp,
    children,
    className: classNameProp,
    style,
    ...rest
  } = componentProps;

  const { mode: modeContext } = useContext(ThemeProviderContext); //useThemeProvider({ theme, mode: modeProp })

  const mode = modeProp ?? modeContext;

  const classNameCombined = cx(styles["theme-provider"], classNameProp, {
    [styles["mode-dark"]]: mode === "dark",
    [styles["mode-light"]]: mode === "light",
  });

  const createCustomThemeStyle = useMemo(() => {
    if (!theme) return;
    const styleObj: { [key: string]: string } = {};
    const themeTokens = {
      ...theme.primitive,
      ...theme.semantic,
      ...theme.component,
    };
    Object.keys(themeTokens).forEach((token) => {
      const light = (themeTokens as TokenObjType)[token].value;
      const dark = (themeTokens as TokenObjType)[token].attributes?.appearance
        ?.dark.value;
      if (!dark) {
        styleObj[`--${kebabize(token)}`] = light;
        return;
      }
      styleObj[`--${kebabize(token)}`] = `light-dark(${light}, ${dark})`;
    });

    return styleObj;
  }, [theme]);

  const styleCombined = {
    ...style,
    ...layoutStyles,
    ...createCustomThemeStyle,
  };

  const value = useMemo(() => ({ theme, mode }), [theme, mode]);

  return (
    <ThemeProviderContext.Provider value={value}>
      <div className={classNameCombined} style={styleCombined} {...rest}>
        {children}
      </div>
    </ThemeProviderContext.Provider>
  );
};

ThemeProvider.displayName = "ThemeProvider";
